// src/pages/profiles/profile-detail-page/profile-detail/PreferencesTab.jsx
import React from "react";
import {
  Grid, Box, Typography, Chip, Stack, Paper, Avatar, useTheme, useMediaQuery
} from "@mui/material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import CakeIcon from '@mui/icons-material/Cake';
import HeightIcon from '@mui/icons-material/Height';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChurchIcon from '@mui/icons-material/Church';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import TranslateIcon from '@mui/icons-material/Translate';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScaleIcon from '@mui/icons-material/Scale';
import SpaIcon from '@mui/icons-material/Spa';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InfoCard from "./components/InfoCard";

// Reusable components (same pattern as OverviewTab)
const IconBox = ({ icon }) => (
  <Box sx={{
    width: 40, height: 40, borderRadius: 2,
    bgcolor: 'primary.alpha20',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    {React.cloneElement(icon, { sx: { color: 'primary.light' } })}
  </Box>
);

const SmallIconBox = ({ icon }) => (
  <Box sx={{
    width: 24, height: 24, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: 'primary.alpha20', mb: 1, mx: 'auto'
  }}>
    {icon}
  </Box>
);

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{
    p: 2, borderRadius: 2, bgcolor: 'primary.alpha10',
    border: 1, borderColor: 'divider', mb: 1.5
  }}>
    <IconBox icon={icon} />
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{value || 'Any'}</Typography>
    </Box>
  </Stack>
);

const Section = ({ title, icon, children, ...props }) => (
  <InfoCard title={title} icon={icon} sx={{
    bgcolor: 'background.default',
    border: 1, borderColor: 'divider',
    height: '100%',
    ...props
  }}>
    {children}
  </InfoCard>
);

const PreferencesTab = ({ profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      
      {/* Age & Physical */}
      <Grid item xs={12} md={6}>
        <Section title="Age & Physical Preferences" icon={<PersonIcon />} sx={{ minHeight: 440 }}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Paper sx={{ 
                p: 2, borderRadius: 2, 
                bgcolor: 'primary.alpha10', 
                border: 1, borderColor: 'divider', 
                textAlign: 'center'
              }}>
                <CakeIcon sx={{ fontSize: 36, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Age Range</Typography>
                <Typography variant="h4" color="primary.light" sx={{ fontWeight: 700 }}>
                  {profile?.preferences_min_age || 'Any'} - {profile?.preferences_max_age || 'Any'}
                </Typography>
              </Paper>
              <InfoRow icon={<HeightIcon />} label="Minimum Height" value={profile?.preferences_min_height} />
              <InfoRow icon={<ScaleIcon />} label="Weight Preference" value={profile?.preferences_weight} />
              <InfoRow icon={<SpaIcon />} label="Complexion" value={profile?.preferences_complexion} />
            </Stack>
          </Box>
        </Section>
      </Grid>

      {/* Marital & Family */}
      <Grid item xs={12} md={6}>
        <Section title="Marital & Family Preferences" icon={<ChildCareIcon />} sx={{ minHeight: 440 }}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Paper sx={{ 
                p: 2, borderRadius: 2, 
                bgcolor: 'primary.alpha10', 
                border: 1, borderColor: 'divider', 
                textAlign: 'center'
              }}>
                <ChildCareIcon sx={{ fontSize: 36, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Marital Status</Typography>
                <Typography variant="h4" color="primary.light" sx={{ fontWeight: 700 }}>
                  {profile?.preferences_marital_status || 'Any'}
                </Typography>
              </Paper>
              <InfoRow icon={<ChildCareIcon />} label="Children Preference" value={profile?.preferences_children} />
              <InfoRow icon={<HomeIcon />} label="Family Type" value={profile?.preferences_family_type} />
              <InfoRow icon={<Diversity3Icon />} label="Family Status" value={profile?.preferences_family_status} />
            </Stack>
          </Box>
        </Section>
      </Grid>

      {/* Education & Career */}
      <Grid item xs={12} md={6}>
        <Section title="Education & Career Preferences" icon={<SchoolIcon />} sx={{ minHeight: 440 }}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Paper sx={{ 
                p: 2, borderRadius: 2, 
                bgcolor: 'primary.alpha10', 
                border: 1, borderColor: 'divider', 
                textAlign: 'center'
              }}>
                <SchoolIcon sx={{ fontSize: 36, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Education Level</Typography>
                <Typography variant="h5" color="primary.light" sx={{ fontWeight: 700 }}>
                  {profile?.preferences_education || 'Any'}
                </Typography>
              </Paper>
              <InfoRow icon={<WorkIcon />} label="Profession" value={profile?.preferences_profession} />
              <InfoRow icon={<BusinessIcon />} label="Employment Type" value={profile?.preferences_employment_type} />
              <InfoRow icon={<AttachMoneyIcon />} label="Income Preference" value={profile?.preferences_income} />
            </Stack>
          </Box>
        </Section>
      </Grid>

      {/* Religious & Location */}
      <Grid item xs={12} md={6}>
        <Section title="Religious & Location Preferences" icon={<ChurchIcon />} sx={{ minHeight: 440 }}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Paper sx={{ 
                p: 2, borderRadius: 2, 
                bgcolor: 'primary.alpha10', 
                border: 1, borderColor: 'divider', 
                textAlign: 'center'
              }}>
                <LocationOnIcon sx={{ fontSize: 36, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Location</Typography>
                <Typography variant="h5" color="primary.light" sx={{ fontWeight: 700 }}>
                  {profile?.preferences_city || 'Any City'}
                </Typography>
              </Paper>
              <InfoRow icon={<ChurchIcon />} label="Religion" value={profile?.preferences_religion} />
              <InfoRow icon={<Diversity3Icon />} label="Sect" value={profile?.preferences_sect} />
              <InfoRow icon={<TranslateIcon />} label="Caste" value={profile?.preferences_caste} />
              <InfoRow icon={<ChurchIcon />} label="Practice Level" value={profile?.preferences_religious_practice} />
            </Stack>
          </Box>
        </Section>
      </Grid>

      {/* Lifestyle & Habits */}
      <Grid item xs={12}>
        <Section title="Lifestyle & Habits Preferences" icon={<FavoriteIcon />} sx={{ minHeight: 200 }}>
          <Box sx={{ p: 1.5 }}>
            <Grid container spacing={1.5}>
              {/* Smoking */}
              <Grid item xs={6} md={3}>
                <Paper sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <Box sx={{ mb: 0.5 }}>
                    {profile?.preferences_smoking === "No" ? 
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} /> : 
                      profile?.preferences_smoking === "Yes" ?
                      <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} /> :
                      <SmokingRoomsIcon sx={{ color: 'primary.light', fontSize: 24 }} />
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Smoking
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {profile?.preferences_smoking || 'Any'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Drinking */}
              <Grid item xs={6} md={3}>
                <Paper sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <Box sx={{ mb: 0.5 }}>
                    {profile?.preferences_drinking === "No" ? 
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} /> : 
                      profile?.preferences_drinking === "Yes" ?
                      <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} /> :
                      <LocalDrinkIcon sx={{ color: 'primary.light', fontSize: 24 }} />
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Drinking
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {profile?.preferences_drinking || 'Any'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Diet */}
              <Grid item xs={6} md={3}>
                <Paper sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <SmallIconBox icon={<RestaurantIcon />} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Diet
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {profile?.preferences_diet || 'Any'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Hobbies */}
              <Grid item xs={6} md={3}>
                <Paper sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <SmallIconBox icon={<FavoriteIcon />} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Hobbies
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {profile?.preferences_hobbies || 'Any'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Section>
      </Grid>

      {/* Summary */}
      <Grid item xs={12}>
        <Paper sx={{ 
          p: { xs: 2, md: 3 }, 
          borderRadius: 3, 
          bgcolor: 'primary.alpha10', 
          border: 1, borderColor: 'divider'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', mr: 2 }}>
                  <FavoriteIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    Partner Preferences
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Summary
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1.5, 
                justifyContent: { xs: 'center', md: 'flex-end' } 
              }}>
                {[
                  { icon: <ChildCareIcon />, label: profile?.preferences_marital_status && profile.preferences_marital_status !== 'Any' ? profile.preferences_marital_status : null },
                  { icon: <SchoolIcon />, label: profile?.preferences_education && profile.preferences_education !== 'Any' ? profile.preferences_education : null },
                  { icon: <ChurchIcon />, label: profile?.preferences_religion && profile.preferences_religion !== 'Any' ? profile.preferences_religion : null },
                  { icon: <LocationOnIcon />, label: profile?.preferences_city && profile.preferences_city !== 'Any' ? profile.preferences_city : null },
                  { icon: <CakeIcon />, label: profile?.preferences_min_age && profile.preferences_max_age ? `${profile.preferences_min_age}-${profile.preferences_max_age} yrs` : null },
                  { icon: <FavoriteIcon />, label: profile?.preferences_diet && profile.preferences_diet !== 'Any' ? profile.preferences_diet : null }
                ].filter(i => i.label).map((item, i) => (
                  <Chip
                    key={i}
                    label={item.label}
                    icon={item.icon}
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 600,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default PreferencesTab;